// Common input sanitizers and helpers
// Use these to ensure consistent input behavior across the app

// Remove all whitespace characters. Useful for fields that must be a single word.
export const sanitizeSingleWord = (val = "") => val.replace(/\s+/g, "")

// During typing: remove leading spaces and collapse multiple spaces to a single space.
// Keeps a trailing space if the user is about to type the next word.
export const sanitizeMultiWordTyping = (val = "") => {
  const noLeading = val.replace(/^\s+/, "")
  return noLeading.replace(/\s{2,}/g, " ")
}

// Fully normalize: collapse all consecutive whitespace to single spaces and trim edges.
// Use on paste and on blur to finalize the value.
export const sanitizeMultiWord = (val = "") => val.replace(/\s+/g, " ").trim()

// Robust space key detection across browsers/environments
export const isSpaceKey = (e) => {
  return e?.key === " " || e?.key === "Space" || e?.key === "Spacebar" || e?.code === "Space" || e?.keyCode === 32
}

// Detect if key represents a digit (0-9), including numpad
export const isDigitKey = (e) => {
  if (!e) return false
  if (/^\d$/.test(e.key)) return true
  const code = e.keyCode || e.which
  // Top row digits without shift
  if (code >= 48 && code <= 57 && !e.shiftKey) return true
  // Numpad digits
  if (code >= 96 && code <= 105) return true
  return false
}

// Remove all digits from string
export const removeDigits = (val = "") => val.replace(/[0-9]+/g, "")

// Neutralize the OS keyboard behavior that turns double-space into ". " (period + space)
// We convert any occurrence of ". " to a single space to preserve word separation
export const neutralizeDoubleSpacePeriod = (val = "") => val.replace(/\.\s/g, " ")

// Allow only letters (a-zA-Z) and spaces during typing: remove leading spaces, collapse multiple spaces,
// and strip any non-letter characters.
export const sanitizeLettersSpacesTyping = (val = "") => {
  const lettersOnly = val.replace(/[^A-Za-z\s]+/g, "")
  const noLeading = lettersOnly.replace(/^\s+/, "")
  return noLeading.replace(/\s{2,}/g, " ")
}

// Fully normalize to only letters and single spaces: strip non-letters, collapse spaces, trim edges.
export const sanitizeLettersSpaces = (val = "") =>
  val
    .replace(/[^A-Za-z\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()

// For multi-word inputs: return true if a space should be blocked at the current caret position
// Rules: block leading space, and block consecutive spaces
export const shouldBlockSpaceForMultiWord = (value = "", selectionStart = 0) => {
  if (selectionStart === 0) return true
  const prevChar = value[selectionStart - 1] || ""
  return prevChar === " "
}

// Example usage (inside a React component):
// const onChange = (e) => setValue(sanitizeMultiWordTyping(e.target.value))
// const onPaste = (e) => { e.preventDefault(); setValue(sanitizeMultiWord(e.clipboardData.getData('text'))) }
// const onKeyDown = (e) => { if (isSpaceKey(e) && shouldBlockSpaceForMultiWord(value, e.target.selectionStart)) e.preventDefault() }
